import React from 'react';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import InputNumber from 'antd/lib/input-number';
import Radio from 'antd/lib/radio';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Switch from 'antd/lib/switch';
import Typography from 'antd/lib/typography';
import List from 'antd/lib/list';
import Modal from 'antd/lib/modal/Modal';
import { EVENT_FREQUENCY, ISettings } from '../../../domain/settings/interface';
import { useCompanySettingsQuery, useUpdateCompanySettingsMutation } from '../../../domain/settings/queries';
import { useAuth } from '../../../domain/auth/useAuth';

const cleanResponseTriggeringOptions = [
    {
        value: EVENT_FREQUENCY.HOUR,
        label: 'one event per hour',
        description: 'Clean Response can trigger an event not more than once per hour',
    },
    {
        value: EVENT_FREQUENCY.DAY,
        label: 'one event per day',
        description: 'Clean Response can trigger an event not more than once per day',
    },
];

const CleanResponseFields: React.FC = () => (
    <Form.Item required label={<Typography.Text strong>Triggering rule:</Typography.Text>} name={['cleanResponse', 'eventPer']}>
        <Radio.Group style={{ width: '100%' }}>
            <List
                grid={{ column: 1 }}
                dataSource={cleanResponseTriggeringOptions}
                renderItem={item => (
                    <List.Item>
                        <Radio value={item.value} key={item.value}>
                            {item.label}
                        </Radio>
                        <List.Item.Meta description={item.description} />
                    </List.Item>
                )}
            />
        </Radio.Group>
    </Form.Item>
);

/** maxDispatchesPerDay = 0 means there is no limit of dispatched events during a day */
const NO_DISPATCH_LIMIT = 0;
const maxDispatchesPerDayPath: ['priceResponse', 'maxDispatchesPerDay'] = ['priceResponse', 'maxDispatchesPerDay'];

const PriceResponseFields: React.FC = () => {
    const form = Form.useFormInstance<ISettings>();
    // useWatch returns undefined on the first render, so fall back to the current form value
    const maxDispatchesPerDay = Form.useWatch(maxDispatchesPerDayPath, form) ?? form.getFieldValue(maxDispatchesPerDayPath);
    const isLimited = maxDispatchesPerDay !== NO_DISPATCH_LIMIT;

    return (
        <>
            <Form.Item
                label={<Typography.Text strong>Cooldown hours:</Typography.Text>}
                name={['priceResponse', 'cooldownHours']}
                extra="Amount of time after an event when a new event can't be created"
                rules={[{ required: true, message: 'Please enter cooldown hours' }]}
            >
                <InputNumber min={0} max={24} precision={0} />
            </Form.Item>
            <Form.Item
                label={<Typography.Text strong>Max dispatches per day:</Typography.Text>}
                extra={
                    isLimited
                        ? 'Number of events that can be created during the day'
                        : 'No limit: any number of events can be created during the day, only cooldown hours are applied'
                }
            >
                <Space>
                    <Switch
                        checked={isLimited}
                        checkedChildren="Limit"
                        unCheckedChildren="No limit"
                        onChange={checked => form.setFieldValue(maxDispatchesPerDayPath, checked ? 1 : NO_DISPATCH_LIMIT)}
                    />
                    <Form.Item noStyle name={maxDispatchesPerDayPath} rules={[{ required: true, message: 'Please enter max dispatches per day' }]}>
                        <InputNumber min={isLimited ? 1 : NO_DISPATCH_LIMIT} max={24} precision={0} disabled={!isLimited} />
                    </Form.Item>
                </Space>
            </Form.Item>
        </>
    );
};

interface ITriggerSettingsModalProps {
    onClose: () => void;
    triggerName: 'priceResponse' | 'cleanResponse';
}

export const TriggerSettingsModal: React.FC<ITriggerSettingsModalProps> = ({ triggerName, onClose }) => {
    const [form] = Form.useForm<ISettings>();
    const { user } = useAuth()!;

    const { data: settings, isLoading } = useCompanySettingsQuery(user!.company_id);
    const { mutate: saveSettings, isLoading: isSaving } = useUpdateCompanySettingsMutation(user!.company_id);

    const onSettingsFormFinish = (updatedSettings: ISettings) => {
        saveSettings({ ...settings, ...updatedSettings }, { onSuccess: onClose });
    };

    return (
        <Modal
            open
            destroyOnHidden
            keyboard={false}
            maskClosable={false}
            title="Trigger Settings"
            width={420}
            onCancel={onClose}
            footer={[
                <Button key="trigger-settings-modal-cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="trigger-settings-submit" type="primary" disabled={isLoading} loading={isSaving} onClick={form.submit}>
                    Save
                </Button>,
            ]}
            data-cy="trigger-settings-modal"
        >
            {isLoading ? (
                <Skeleton active />
            ) : (
                <Form form={form} name="settings-form" preserve={false} initialValues={settings} onFinish={onSettingsFormFinish} layout="vertical">
                    {triggerName === 'priceResponse' ? <PriceResponseFields /> : <CleanResponseFields />}
                </Form>
            )}
        </Modal>
    );
};
